// EstateTopLines.js — "Top 200 lines" across the whole estate.
//
// Unlike BranchStockTabs (one branch, drugs down the rows), this is the
// estate view the button next to Idle stock opens: drugs down the rows,
// branches across the columns, ranked by total packets or total value
// dispensed over each branch's own last three FULL months of usage data -
// not necessarily the same three calendar months for every branch, since a
// branch whose newest month is not yet fully reported is a month behind the
// others (see chooseUsageWindow in estate_model.js). rows already carries
// each branch's own trimmed monthly data, so the ranking and totals need no
// further adjustment here - this file only needs to SHOW which months each
// column actually rests on.

import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { displayName } from "./nameMask";
import { monthsLabel } from "./estate_model";

const money = (n) =>
  `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const th = { textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: "6px 8px", whiteSpace: "nowrap" };
const td = { borderBottom: "1px solid #f1f5f9", padding: "6px 8px", whiteSpace: "nowrap" };

function SortTh({ label, sub, colKey, sort, setSort, align, title }) {
  const active = sort.key === colKey;
  return (
    <th
      style={{ ...th, cursor: "pointer", userSelect: "none", textAlign: align || "left", position: "sticky", top: 0, background: "#f1f5f9" }}
      title={title}
      onClick={() => setSort((s) => (s.key === colKey ? { key: colKey, dir: s.dir === "asc" ? "desc" : "asc" } : { key: colKey, dir: "desc" }))}
    >
      {label}{active ? (sort.dir === "asc" ? " \u25b2" : " \u25bc") : ""}
      {sub && <div style={{ fontSize: "0.75em", fontWeight: 400, color: "#94a3b8" }}>{sub}</div>}
    </th>
  );
}

export default function EstateTopLines({ rows = [], members = [], masked, orgLabel, usageWindows = {} }) {
  const [basis, setBasis] = useState("value"); // "value" | "packets"
  const [sort, setSort] = useState({ key: "total", dir: "desc" });

  // Only branches with any usable data get a column — an excluded branch's
  // rows are already zero everywhere else on this screen, and giving it a
  // column here would just be an empty column for every one of the 200 rows.
  const branchList = useMemo(() => {
    const ids = new Set(rows.filter((r) => r.usable).map((r) => r.pharmacyId));
    return members
      .filter((m) => ids.has(m.pharmacyId))
      .sort((a, b) => String(a.pharmacyName).localeCompare(String(b.pharmacyName)));
  }, [rows, members]);

  const ranked = useMemo(() => {
    const byDrug = new Map();
    for (const r of rows) {
      if (!r.usable) continue;
      const totalUnits = (r.monthly || []).reduce((t, m) => t + (m.qty || 0), 0);
      if (totalUnits <= 0) continue;
      const packets = Math.ceil((r.packSize > 0 ? totalUnits / r.packSize : totalUnits) - 1e-9);
      const value = totalUnits * (r.unitValue || 0);

      if (!byDrug.has(r.cleanName)) {
        byDrug.set(r.cleanName, { cleanName: r.cleanName, drugName: r.drugName, perBranch: {}, totalPackets: 0, totalValue: 0 });
      }
      const d = byDrug.get(r.cleanName);
      // Longest name generally carries the most detail (strength, form) - a
      // fair enough tie-break when the same product is named slightly
      // differently between branches.
      if (String(r.drugName).length > String(d.drugName).length) d.drugName = r.drugName;
      d.perBranch[r.pharmacyId] = { packets, value };
      d.totalPackets += packets;
      d.totalValue += value;
    }

    const metricKey = basis === "value" ? "totalValue" : "totalPackets";
    return [...byDrug.values()].sort((a, b) => b[metricKey] - a[metricKey]).slice(0, 200);
  }, [rows, basis]);

  const sorted = useMemo(() => {
    const copy = [...ranked];
    copy.sort((a, b) => {
      let va, vb;
      if (sort.key === "name") { va = a.drugName; vb = b.drugName; }
      else if (sort.key === "total") { va = basis === "value" ? a.totalValue : a.totalPackets; vb = basis === "value" ? b.totalValue : b.totalPackets; }
      else {
        // a branch column - sort key is that branch's pharmacyId
        va = (a.perBranch[sort.key]?.[basis === "value" ? "value" : "packets"]) || 0;
        vb = (b.perBranch[sort.key]?.[basis === "value" ? "value" : "packets"]) || 0;
      }
      let cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [ranked, sort, basis]);

  const cell = (d, pharmacyId) => {
    const v = d.perBranch[pharmacyId];
    if (!v) return "";
    return basis === "value" ? money(v.value) : v.packets;
  };

  const doExport = () => {
    const aoa = [];
    aoa.push([orgLabel || ""]);
    aoa.push([new Date().toLocaleDateString("en-GB")]);
    aoa.push([`Top 200 lines — ${basis === "value" ? "by value" : "by packet numbers"} — each branch's own last three full months`]);
    aoa.push([]);
    const header = [
      "Drug",
      ...branchList.map((m) => {
        const lbl = monthsLabel(usageWindows[m.pharmacyId]?.months || []);
        return `${displayName(m.pharmacyName, masked)}${lbl ? ` (${lbl})` : ""}`;
      }),
      "Total",
    ];
    aoa.push(header);
    sorted.forEach((d) => {
      const row = [d.drugName];
      branchList.forEach((m) => {
        const v = d.perBranch[m.pharmacyId];
        row.push(v ? (basis === "value" ? Number(v.value.toFixed(2)) : v.packets) : "");
      });
      row.push(basis === "value" ? Number(d.totalValue.toFixed(2)) : d.totalPackets);
      aoa.push(row);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Top 200 lines");
    XLSX.writeFile(wb, `top-200-lines-estate-${basis}.xlsx`);
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #dfe3e8", borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700 }}>Top 200 lines — whole estate, last three full months</div>
        <label><input type="radio" checked={basis === "value"} onChange={() => setBasis("value")} /> Top in value</label>
        <label><input type="radio" checked={basis === "packets"} onChange={() => setBasis("packets")} /> Top in packet numbers</label>
        <span style={{ flex: 1 }} />
        <button onClick={doExport} style={{ padding: "7px 12px", fontWeight: 600 }}>Export to Excel</button>
      </div>
      <div style={{ padding: "6px 14px", fontSize: "0.78em", color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>
        Each branch is ranked on its own last three FULL months — a branch whose newest month isn't fully reported
        yet is a month behind the others, shown under its column heading.
      </div>
      <div style={{ maxHeight: 520, overflow: "auto" }}>
        <table style={{ borderCollapse: "collapse", fontSize: "0.85em", width: "100%" }}>
          <thead>
            <tr>
              <SortTh label="Drug" colKey="name" sort={sort} setSort={setSort} />
              {branchList.map((m) => (
                <SortTh key={m.pharmacyId} label={displayName(m.pharmacyName, masked)}
                  sub={monthsLabel(usageWindows[m.pharmacyId]?.months || [])}
                  colKey={m.pharmacyId}
                  sort={sort} setSort={setSort} align="right"
                  title={`${displayName(m.pharmacyName, masked)} — ${basis === "value" ? "value" : "packets"} dispensed over its own last three full months (${monthsLabel(usageWindows[m.pharmacyId]?.months || [])})`} />
              ))}
              <SortTh label="Total" colKey="total" sort={sort} setSort={setSort} align="right" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((d, i) => (
              <tr key={i}>
                <td style={td}>{d.drugName}</td>
                {branchList.map((m) => (
                  <td key={m.pharmacyId} style={{ ...td, textAlign: "right", color: d.perBranch[m.pharmacyId] ? "#1f2937" : "#cbd5e1" }}>
                    {cell(d, m.pharmacyId) || "—"}
                  </td>
                ))}
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>
                  {basis === "value" ? money(d.totalValue) : d.totalPackets}
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={branchList.length + 2} style={{ ...td, textAlign: "center", color: "#64748b" }}>No dispensing recorded across the estate in the last three months.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
